import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence, useReducedMotion } from "motion/react";
import { IconShoppingCart } from "@tabler/icons-react";
import { Button, Badge, cn } from "@lemonadetraining/web-ui";
import { cartActions } from "@lemonadetraining/web-shared";

type FloatingCartProps = {
  /* Only the landing page defines --brand-accent (brandTheme.ts). Elsewhere an
   * unguarded var() computes to transparent, so the tint is opt-in and the
   * default Button colors carry every other surface, dark theme included. */
  brandAccent?: boolean;
  /* Positioning override. A host whose toast lane is bottom-right must move the
   * button out of it, or the toast covers the only cart trigger. */
  className?: string;
};

function FloatingCart({ brandAccent = false, className }: FloatingCartProps) {
  const dispatch = useDispatch();
  const shouldReduceMotion = useReducedMotion();
  const itemCount = useSelector((state: any) => state.cart.cartItems.length);

  const handleClick = () => {
    dispatch(cartActions.showCheckout(true));
  };

  return (
    <>
      <AnimatePresence>
        {itemCount > 0 && (
          <motion.div
            key="floating-cart"
            initial={shouldReduceMotion ? { opacity: 0 } : { opacity: 0, y: 100 }}
            animate={shouldReduceMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
            exit={shouldReduceMotion ? { opacity: 0 } : { opacity: 0, y: 100 }}
            transition={
              shouldReduceMotion
                ? { duration: 0.15 }
                : { type: "spring", stiffness: 260, damping: 20 }
            }
            className={cn("fixed z-40", "bottom-6 right-6 md:bottom-8 md:right-8", className)}
            data-testid="floating-cart-container"
          >
            <Button
              onClick={handleClick}
              className="relative rounded-full shadow-xl h-14 w-14"
              style={
                brandAccent ? { backgroundColor: "var(--brand-accent)", color: "white" } : undefined
              }
              size="icon"
              aria-label={`View shopping cart, ${itemCount} ${itemCount === 1 ? "item" : "items"}`}
              data-testid="floating-cart"
            >
              {/* Pulse animation on item count change */}
              <motion.div
                key={itemCount}
                animate={shouldReduceMotion ? {} : { scale: [1, 1.15, 1] }}
                transition={
                  shouldReduceMotion ? { duration: 0 } : { duration: 0.3, ease: "easeInOut" }
                }
              >
                <IconShoppingCart size={24} />
              </motion.div>

              {/* Count badge */}
              <Badge
                variant={brandAccent ? "default" : "secondary"}
                className="absolute -top-1 -right-1 h-5 min-w-[20px] px-1 flex items-center justify-center text-xs"
                data-testid="cart-count"
              >
                {shouldReduceMotion ? (
                  itemCount
                ) : (
                  <motion.span
                    key={itemCount}
                    initial={{ scale: 0.5 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", stiffness: 300, damping: 15 }}
                  >
                    {itemCount}
                  </motion.span>
                )}
              </Badge>
            </Button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Screen reader announcement — always in DOM for live region to work */}
      <div aria-live="polite" className="sr-only" data-testid="cart-announcement">
        {itemCount > 0
          ? `Cart updated. ${itemCount} ${itemCount === 1 ? "item" : "items"} in cart.`
          : ""}
      </div>
    </>
  );
}

export default FloatingCart;
